import LegalLayout, { Section } from "@/components/legal/LegalLayout";
import { usePublicSettings } from "@/hooks/usePublicSettings";
import usePageMeta from "@/hooks/usePageMeta";

export default function Terms() {
  const { settings } = usePublicSettings();
  const supportEmail = settings.CONTACT_EMAIL_SUPPORT || "support@colourmatch.au";
  usePageMeta({
    title: "Terms of Service — ColourMatch AI Paint Visualiser",
    description: "The terms that apply when you use ColourMatch — the AI paint visualiser software platform for Australian homeowners.",
    path: "/terms",
  });
  return (
    <LegalLayout
      title="Terms of Service"
      lastUpdated="February 2026"
      testid="terms-page"
      intro={
        <span><strong>Draft for review.</strong> These Terms are a working draft and should be reviewed by an Australian lawyer before live trading.</span>
      }
    >
      <Section heading="1. About ColourMatch">
        <p>
          ColourMatch (“ColourMatch”, “we”, “us”, “our”) provides an AI-powered paint colour
          visualisation service that lets users upload a photo of a surface and preview how
          different paint colours would look on that surface. By creating an account or using
          the service you agree to these Terms of Service (“Terms”).
        </p>
      </Section>

      <Section heading="2. Who can use the service">
        <p>
          You must be at least 16 years of age and have legal capacity to enter a contract.
          By using ColourMatch you confirm the information you provide during registration is
          accurate, current and complete.
        </p>
      </Section>

      <Section heading="3. Your account">
        <p>
          You are responsible for safeguarding your account credentials and for all activity
          that occurs under your account. Notify us at <strong>{supportEmail}</strong> if you suspect
          any unauthorised use. We may suspend or terminate accounts that we reasonably believe
          have been used to violate these Terms.
        </p>
      </Section>

      <Section heading="4. Photos you upload">
        <p>
          You retain ownership of every photo you upload. By uploading, you grant ColourMatch a
          limited, non-exclusive licence to store, process and transform your photo solely to
          deliver the visualisation service to you (including transmitting it to our AI
          rendering provider).
        </p>
        <p>
          You confirm that you have the right to upload each photo and that the photo does not
          infringe any third-party rights or contain unlawful content (including but not
          limited to nudity, violence, or content that identifies a person without consent).
        </p>
      </Section>

      <Section heading="5. AI-generated renders">
        <p>
          ColourMatch uses third-party AI models to generate recoloured versions of your photo.
          AI output is provided on a <em>best-effort</em> basis. We do not guarantee that the
          rendered colour will match any physical paint product exactly, and screen colour
          will vary by device. Always confirm the final paint colour with a physical paint
          sample before purchasing or applying paint.
        </p>
      </Section>

      <Section heading="6. Free preview and paid unlock">
        <p>
          A free, watermarked preview render is included per project. To remove the watermark,
          test additional colours and download in HD you may purchase a one-time unlock at the
          price displayed on the pricing page (currently AUD $29.99 per project, lifetime
          access to that project). Prices may change for new projects but will not change for
          projects you have already unlocked.
        </p>
      </Section>

      <Section heading="7. Acceptable use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Upload content you do not have the right to use;</li>
          <li>Attempt to reverse-engineer, scrape or otherwise interfere with the service;</li>
          <li>Use ColourMatch to generate misleading, harmful or unlawful imagery;</li>
          <li>Resell or commercialise the renders without our written permission.</li>
        </ul>
      </Section>

      <Section heading="8. Australian Consumer Law">
        <p>
          Nothing in these Terms excludes, restricts or modifies any guarantee, right or
          remedy you may have under the Australian Consumer Law (Schedule 2 of the Competition
          and Consumer Act 2010 (Cth)) or any other applicable law that cannot be lawfully
          excluded.
        </p>
      </Section>

      <Section heading="9. Limitation of liability">
        <p>
          To the maximum extent permitted by law, ColourMatch's total liability arising out of
          or in connection with the service is limited to the amount you paid us in the 12
          months prior to the event giving rise to the claim. We are not liable for indirect,
          special, incidental or consequential losses.
        </p>
      </Section>

      <Section heading="10. Termination">
        <p>
          You may close your account at any time by contacting us. We may suspend or terminate
          your access if you breach these Terms. Sections that by their nature should survive
          termination (including ownership, limitation of liability and disputes) will survive.
        </p>
      </Section>

      <Section heading="11. Changes to these Terms">
        <p>
          We may update these Terms from time to time. The “Last updated” date above will
          reflect the latest version. Material changes will be notified by email or in-app.
          Continued use of the service after changes take effect constitutes acceptance.
        </p>
      </Section>

      <Section heading="12. Governing law">
        <p>
          These Terms are governed by the laws of the State of Victoria, Australia. You and
          ColourMatch submit to the non-exclusive jurisdiction of the courts of Victoria.
        </p>
      </Section>

      <Section heading="13. Contact">
        <p>Questions about these Terms? <strong>{supportEmail}</strong></p>
      </Section>
    </LegalLayout>
  );
}
